(function () {
  'use strict';

  if (window.SolsticeCompositionImages) return;

  // La v2 invalide les anciens jetons qui pouvaient référencer un produit
  // temporaire créé avant que son image de composition soit exploitable.
  var STORAGE_PREFIX = 'solstice_composition_image_v2:';
  var MAX_STORAGE_TTL_MS = 24 * 60 * 60 * 1000;
  // Le checkout affiche une petite vignette. 360 x 540 conserve plus de 5 fois
  // sa résolution d'affichage habituelle, tout en divisant presque par deux le
  // nombre de pixels à encoder et à envoyer par rapport à l'ancien 500 x 750.
  var CANVAS_WIDTH = 360;
  var CANVAS_HEIGHT = 540;
  var MAX_UPLOAD_BYTES = 192 * 1024;
  var MAX_CONCURRENT_JOBS = 2;
  var states = new Map();
  var jobs = new Map();
  var queuedJobs = [];
  var runningJobCount = 0;

  function now() {
    return window.performance && typeof window.performance.now === 'function'
      ? window.performance.now()
      : Date.now();
  }

  function report(configId, status, startedAt, extra) {
    var detail = Object.assign({
      configId: String(configId),
      status: status,
      durationMs: Math.round((now() - startedAt) * 10) / 10
    }, extra || {});

    window.dispatchEvent(new CustomEvent('solstice:composition-image-metric', { detail: detail }));

    if (window.console && typeof window.console.debug === 'function') {
      window.console.debug('[Composition image]', detail);
    }
  }

  function scheduleIdle(callback) {
    if (typeof window.requestIdleCallback === 'function') {
      var idleId = window.requestIdleCallback(callback, { timeout: 2000 });
      return function () {
        if (typeof window.cancelIdleCallback === 'function') {
          window.cancelIdleCallback(idleId);
        }
      };
    }

    var timeoutId = window.setTimeout(callback, 750);
    return function () { window.clearTimeout(timeoutId); };
  }

  function storageKey(configId) {
    return STORAGE_PREFIX + String(configId);
  }

  function normalizeComponentIds(componentIds) {
    if (!Array.isArray(componentIds) || componentIds.length === 0) return null;

    var normalized = componentIds.map(function (id) {
      return Number(id);
    });

    if (normalized.some(function (id) { return !Number.isSafeInteger(id) || id <= 0; })) {
      return null;
    }

    return normalized.sort(function (a, b) { return a - b; }).join(',');
  }

  function finiteStyleNumber(value) {
    var parsed = Number.parseFloat(String(value == null ? '' : value).replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }

  function normalizeLayers(layers) {
    if (!Array.isArray(layers) || layers.length === 0) return null;

    var normalized = layers.map(function (layer, index) {
      var style = layer && layer.style ? layer.style : {};
      var left = finiteStyleNumber(style.left);
      var top = finiteStyleNumber(style.top);
      var width = finiteStyleNumber(style.width);
      var zIndex = finiteStyleNumber(style.zIndex);
      var opacity = finiteStyleNumber(style.opacity);

      if (
        !layer || typeof layer.src !== 'string' || !layer.src.trim() ||
        left === null || top === null || width === null || width <= 0
      ) {
        return null;
      }

      return {
        id: String(layer.id == null ? index : layer.id),
        src: layer.src.trim(),
        variantIds: Array.isArray(layer.variantIds)
          ? layer.variantIds.map(function (id) { return Number(id); })
          : [],
        left: left,
        top: top,
        width: width,
        zIndex: zIndex === null ? 0 : zIndex,
        opacity: opacity === null ? 1 : Math.max(0, Math.min(1, opacity)),
        sourceIndex: index
      };
    });

    if (
      normalized.some(function (layer) {
        return layer === null || layer.variantIds.length === 0 ||
          layer.variantIds.some(function (id) { return !Number.isSafeInteger(id) || id <= 0; });
      })
    ) return null;

    return normalized.sort(function (a, b) {
      return a.zIndex - b.zIndex || a.sourceIndex - b.sourceIndex;
    });
  }

  function getUploadEndpoint() {
    var config = window.SolsticeCompositionImageConfig || {};
    if (config.mode !== 'staging' || typeof config.stagingUrl !== 'string' || !config.stagingUrl.trim()) {
      return null;
    }

    try {
      var endpoint = new URL(config.stagingUrl.trim());
      var isLocalDevelopment = endpoint.protocol === 'http:' &&
        ['localhost', '127.0.0.1', '::1'].includes(endpoint.hostname);

      if (endpoint.protocol !== 'https:' && !isLocalDevelopment) return null;

      endpoint.pathname = endpoint.pathname.replace(/\/+$/, '').replace(/\/[^/]*$/, '/composition-image');
      endpoint.search = '';
      endpoint.hash = '';
      return endpoint.href;
    } catch (error) {
      return null;
    }
  }

  function canonicalLayout(backgroundUrl, material, layers, photoComponentKey) {
    if (photoComponentKey) return JSON.stringify({ version: 3, kind: 'catalog-photo', image: backgroundUrl, material: material, components: photoComponentKey, width: CANVAS_WIDTH, height: CANVAS_HEIGHT });
    return JSON.stringify({
      // Incluse dans le hash envoyé au backend : ce changement force aussi la
      // création d'un nouveau produit illustré au lieu de réutiliser l'ancien.
      version: 2,
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
      background: backgroundUrl,
      material: material,
      layers: layers.map(function (layer) {
        return {
          id: layer.id,
          src: layer.src,
          variantIds: layer.variantIds,
          left: Number(layer.left.toFixed(4)),
          top: Number(layer.top.toFixed(4)),
          width: Number(layer.width.toFixed(4)),
          zIndex: layer.zIndex,
          opacity: Number(layer.opacity.toFixed(4))
        };
      })
    });
  }

  async function sha256Hex(value) {
    if (!window.crypto || !window.crypto.subtle || typeof TextEncoder === 'undefined') {
      throw new Error('sha256_unavailable');
    }

    var bytes = new TextEncoder().encode(value);
    var digest = await window.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest)).map(function (byte) {
      return byte.toString(16).padStart(2, '0');
    }).join('');
  }

  function loadImageStrict(src, job) {
    return new Promise(function (resolve, reject) {
      var image = new Image();
      var settled = false;
      var timeout;

      function cleanup() {
        window.clearTimeout(timeout);
        job.imageCancellers.delete(cancel);
        image.onload = null;
        image.onerror = null;
      }

      function fail(error) {
        if (settled) return;
        settled = true;
        cleanup();
        reject(error);
      }

      function cancel() {
        if (settled) return;
        fail(new Error('job_cancelled'));
        image.src = '';
      }

      if (job.cancelled) {
        fail(new Error('job_cancelled'));
        return;
      }

      job.imageCancellers.add(cancel);
      timeout = window.setTimeout(function () {
        fail(new Error('image_timeout'));
        image.src = '';
      }, 8000);

      image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      image.onload = function () {
        if (!image.naturalWidth || !image.naturalHeight) {
          fail(new Error('image_empty'));
          return;
        }
        if (settled) return;
        settled = true;
        cleanup();
        resolve(image);
      };
      image.onerror = function () {
        fail(new Error('image_load_failed'));
      };
      image.src = src;
    });
  }

  function canvasToBlob(canvas, type, quality) {
    return new Promise(function (resolve, reject) {
      try {
        canvas.toBlob(function (blob) {
          if (blob) resolve(blob);
          else reject(new Error('canvas_export_failed'));
        }, type, quality);
      } catch (error) {
        reject(error);
      }
    });
  }

  function shortRetryDelay(response) {
    if (!response || response.status !== 429 || !response.headers ||
        typeof response.headers.get !== 'function') return 0;

    var rawValue = response.headers.get('Retry-After');
    var seconds = Number.parseFloat(String(rawValue == null ? '' : rawValue));
    if (!Number.isFinite(seconds) || seconds <= 0) return 0;

    var delayMs = Math.ceil(seconds * 1000);
    // `IMAGE_UPLOAD_BUSY` vaut actuellement 2 s. Une vraie limite minute ne
    // doit pas retenir la cliente dans le panier pendant des dizaines de secondes.
    return delayMs <= 3000 ? Math.max(250, delayMs) : 0;
  }

  async function renderBlob(backgroundUrl, layers, job) {
    // Tout est chargé en parallèle. Une seule image manquante annule le rendu :
    // aucune vignette partielle ne doit pouvoir atteindre le checkout.
    var images = await Promise.all(
      [loadImageStrict(backgroundUrl, job)].concat(layers.map(function (layer) {
        return loadImageStrict(layer.src, job);
      }))
    );

    if (job.cancelled) throw new Error('job_cancelled');

    var canvas = document.createElement('canvas');
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    var context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('canvas_unavailable');

    try {
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      if (job.photoUrl) {
        var photoScale = Math.min(CANVAS_WIDTH / images[0].naturalWidth, CANVAS_HEIGHT / images[0].naturalHeight);
        var photoWidth = images[0].naturalWidth * photoScale;
        var photoHeight = images[0].naturalHeight * photoScale;
        context.drawImage(images[0], (CANVAS_WIDTH - photoWidth) / 2, (CANVAS_HEIGHT - photoHeight) / 2, photoWidth, photoHeight);
      } else {
        context.drawImage(images[0], 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      }

      layers.forEach(function (layer, index) {
        var image = images[index + 1];
        var width = (layer.width / 100) * CANVAS_WIDTH;
        var height = width * image.naturalHeight / image.naturalWidth;
        var centerX = (layer.left / 100) * CANVAS_WIDTH;
        var centerY = (layer.top / 100) * CANVAS_HEIGHT;

        context.globalAlpha = layer.opacity;
        context.drawImage(image, centerX - width / 2, centerY - height / 2, width, height);
      });
      context.globalAlpha = 1;

      // Le chemin REST utilisé pour précréer le produit fantôme documente JPEG,
      // PNG et GIF. JPEG est adapté au fond photographique et moins coûteux à
      // encoder ici ; une seconde passe n'arrive que si la limite est dépassée.
      var blob = await canvasToBlob(canvas, 'image/jpeg', 0.74);
      if (job.cancelled) throw new Error('job_cancelled');
      if (blob.size > MAX_UPLOAD_BYTES) {
        blob = await canvasToBlob(canvas, 'image/jpeg', 0.58);
        if (job.cancelled) throw new Error('job_cancelled');
      }
      if (blob.type !== 'image/jpeg' || blob.size > MAX_UPLOAD_BYTES) {
        throw new Error('image_too_large');
      }

      return blob;
    } finally {
      context.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      canvas.width = 0;
      canvas.height = 0;
    }
  }

  function expiryTimestamp(value) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value < 1000000000000 ? value * 1000 : value;
    }

    var parsed = Date.parse(String(value || ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function removeStored(configId) {
    try {
      window.localStorage.removeItem(storageKey(configId));
    } catch (error) {}
    try {
      window.sessionStorage.removeItem(storageKey(configId));
    } catch (error) {}
  }

  function isReadyStateValid(state, componentKey) {
    return Boolean(
      state && state.status === 'ready' &&
      (!componentKey || state.componentKey === componentKey) &&
      typeof state.imageToken === 'string' &&
      state.imageToken.length >= 20 && state.imageToken.length <= 4096 &&
      Number.isFinite(state.expiresAt) && state.expiresAt > Date.now()
    );
  }

  function persistReady(configId, readyState) {
    var nowMs = Date.now();
    var storedState = Object.assign({}, readyState, {
      // Même si le serveur émettait un jeton anormalement long, sa copie locale
      // est volontairement bornée. La date serveur reste la borne la plus courte.
      expiresAt: Math.min(readyState.expiresAt, nowMs + MAX_STORAGE_TTL_MS),
      storedAt: nowMs
    });

    states.set(String(configId), storedState);
    try {
      // localStorage rend le jeton signé disponible après reload et dans un
      // nouvel onglet. Il ne contient ni donnée cliente ni image en base64.
      window.localStorage.setItem(storageKey(configId), JSON.stringify(storedState));
    } catch (error) {
      // Safari privé peut refuser localStorage : sessionStorage garde alors le
      // comportement précédent, sans rendre le checkout dépendant du stockage.
      try {
        window.sessionStorage.setItem(storageKey(configId), JSON.stringify(storedState));
      } catch (sessionError) {}
    }
  }

  async function runJob(job, startedAt) {
    try {
      var layoutDigest = await sha256Hex(canonicalLayout(job.backgroundUrl, job.material, job.layers, job.photoUrl ? job.componentKey : null));
      if (job.cancelled) throw new Error('job_cancelled');

      var blob = await renderBlob(job.backgroundUrl, job.layers, job);
      if (job.cancelled) throw new Error('job_cancelled');

      var controller = new AbortController();
      job.controller = controller;
      var timeout = window.setTimeout(function () { controller.abort(); }, 55000);
      var response;

      try {
        for (var uploadAttempt = 0; uploadAttempt < 2; uploadAttempt += 1) {
          response = await fetch(job.endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': blob.type,
              'X-Solstice-Checkout-Version': '2',
              'X-Composition-Component-Ids': job.componentKey,
              'X-Composition-Layout-Digest': layoutDigest
            },
            body: blob,
            signal: controller.signal
          });

          if (response.status === 201) break;
          var retryDelayMs = uploadAttempt === 0 ? shortRetryDelay(response) : 0;
          if (!retryDelayMs) break;

          await new Promise(function (resolve) {
            window.setTimeout(resolve, retryDelayMs);
          });
          if (job.cancelled) throw new Error('job_cancelled');
        }
      } finally {
        window.clearTimeout(timeout);
        job.controller = null;
      }

      if (job.cancelled) throw new Error('job_cancelled');
      if (response.status !== 201) throw new Error('upload_http_' + response.status);

      var data = await response.json();
      if (job.cancelled) throw new Error('job_cancelled');
      var expiresAt = expiryTimestamp(data && data.expiresAt);
      if (
        !data || data.success !== true || data.status !== 'ready' ||
        typeof data.imageToken !== 'string' || data.imageToken.length < 20 || data.imageToken.length > 4096 ||
        data.layoutDigest !== layoutDigest || expiresAt <= Date.now()
      ) {
        throw new Error('upload_response_invalid');
      }

      persistReady(job.configId, {
        status: 'ready',
        imageToken: data.imageToken,
        componentKey: job.componentKey,
        layoutDigest: layoutDigest,
        expiresAt: expiresAt
      });
      report(job.configId, 'ready', startedAt, { bytes: blob.size });
    } catch (error) {
      if (job.cancelled) return;
      states.set(job.configId, { status: 'failed' });
      report(job.configId, 'fallback', startedAt, {
        reason: error && error.message ? error.message : 'unknown'
      });
    } finally {
      job.imageCancellers.forEach(function (cancel) { cancel(); });
      job.imageCancellers.clear();
      if (jobs.get(job.configId) === job) jobs.delete(job.configId);
    }
  }

  function startQueuedJob(job) {
    job.queued = false;
    job.running = true;
    runningJobCount += 1;
    Promise.resolve(runJob(job, job.startedAt)).finally(function () {
      job.running = false;
      runningJobCount = Math.max(0, runningJobCount - 1);
      drainJobQueue();
    });
  }

  function drainJobQueue() {
    while (runningJobCount < MAX_CONCURRENT_JOBS && queuedJobs.length > 0) {
      var job = queuedJobs.shift();
      if (!job || job.cancelled || jobs.get(job.configId) !== job) continue;
      startQueuedJob(job);
    }
  }

  function enqueueJob(job) {
    if (!job || job.cancelled || job.queued || job.running) return;
    job.queued = true;
    queuedJobs.push(job);
    drainJobQueue();
  }

  function cancelJob(job, shouldReport) {
    if (!job || job.cancelled) return false;

    job.cancelled = true;
    if (job.cancelIdle) job.cancelIdle();
    job.imageCancellers.forEach(function (cancel) { cancel(); });
    job.imageCancellers.clear();
    if (job.controller) job.controller.abort();
    if (jobs.get(job.configId) === job) jobs.delete(job.configId);
    states.set(job.configId, { status: 'cancelled', componentKey: job.componentKey });

    if (shouldReport) report(job.configId, 'cancelled', job.startedAt);
    return true;
  }

  function schedule(input) {
    var startedAt = now();
    var configId = input && String(input.configId || '').trim();
    var photoUrl = validPhotoUrl(input && input.photoUrl);
    var backgroundUrl = photoUrl || (input && typeof input.backgroundUrl === 'string'
      ? input.backgroundUrl.trim()
      : '');
    var material = input && typeof input.material === 'string' ? input.material.trim() : '';
    var componentKey = normalizeComponentIds(input && input.componentIds);
    var layers = photoUrl ? [] : normalizeLayers(input && input.layers);
    var endpoint = getUploadEndpoint();

    var representedComponentKey = photoUrl ? componentKey : layers
      ? normalizeComponentIds(layers.reduce(function (ids, layer) {
          return ids.concat(layer.variantIds);
        }, []))
      : null;

    if (
      !configId || !backgroundUrl || !material || !componentKey || !layers || !endpoint ||
      representedComponentKey !== componentKey
    ) {
      report(configId || 'unknown', 'skipped', startedAt, { reason: 'invalid_or_disabled' });
      return false;
    }

    // Plusieurs lectures de cart.js peuvent survenir pendant l'ouverture du
    // drawer. Elles ne doivent ni relancer un upload déjà prêt, ni annuler un
    // rendu identique encore en cours.
    if (input.onlyIfMissing === true) {
      if (getReadyToken(configId, componentKey.split(','))) return true;
      var currentJob = jobs.get(configId);
      if (currentJob && !currentJob.cancelled && currentJob.componentKey === componentKey) {
        return true;
      }
    }

    // L'appelant peut réutiliser ses objets Vue puis vider le configurateur : le
    // job ne conserve ici que des primitives immuables.
    var job = {
      configId: configId,
      backgroundUrl: backgroundUrl,
      photoUrl: photoUrl,
      material: material,
      componentKey: componentKey,
      layers: layers,
      endpoint: endpoint,
      startedAt: startedAt,
      cancelled: false,
      cancelIdle: null,
      controller: null,
      imageCancellers: new Set(),
      queued: false,
      running: false
    };

    cancelJob(jobs.get(configId), false);
    states.set(configId, { status: 'pending', componentKey: componentKey });
    removeStored(configId);

    jobs.set(configId, job);
    if (input.startImmediately === true) {
      // Après un ajout panier réussi, l'aperçu peut être préparé tout de suite
      // en arrière-plan : aucun await n'est renvoyé au configurateur.
      enqueueJob(job);
    } else {
      job.cancelIdle = scheduleIdle(function () {
        job.cancelIdle = null;
        if (job.cancelled) return;
        enqueueJob(job);
      });
    }
    return true;
  }

  function readStored(configId) {
    var stores = [];
    try {
      if (window.localStorage) stores.push(window.localStorage);
    } catch (error) {}
    try {
      if (window.sessionStorage) stores.push(window.sessionStorage);
    } catch (error) {}

    for (var index = 0; index < stores.length; index += 1) {
      try {
        var raw = stores[index].getItem(storageKey(configId));
        if (!raw) continue;
        var parsed = JSON.parse(raw);
        if (isReadyStateValid(parsed)) return parsed;
        stores[index].removeItem(storageKey(configId));
      } catch (error) {
        // Un stockage indisponible ou une entrée corrompue n'affecte jamais le
        // panier : on essaie simplement le stockage suivant.
      }
    }

    return null;
  }

  function getReadyToken(configId, componentIds) {
    var normalizedConfigId = String(configId || '').trim();
    var componentKey = normalizeComponentIds(componentIds);
    if (!normalizedConfigId || !componentKey) return null;

    var state = states.get(normalizedConfigId);
    if (!isReadyStateValid(state, componentKey)) {
      state = readStored(normalizedConfigId);
      if (isReadyStateValid(state, componentKey)) {
        states.set(normalizedConfigId, state);
      } else {
        if (state && state.expiresAt <= Date.now()) removeStored(normalizedConfigId);
        return null;
      }
    }

    return state.imageToken;
  }

  function startPendingNow(configId) {
    var normalizedConfigId = String(configId || '').trim();
    var job = jobs.get(normalizedConfigId);
    if (!job || job.cancelled) return false;

    if (job.cancelIdle) {
      job.cancelIdle();
      job.cancelIdle = null;
    }
    enqueueJob(job);
    return true;
  }

  function waitForReadyToken(configId, componentIds, timeoutMs) {
    var normalizedConfigId = String(configId || '').trim();
    var componentKey = normalizeComponentIds(componentIds);
    var parsedTimeout = Number(timeoutMs);
    var boundedTimeout = Number.isFinite(parsedTimeout)
      ? Math.max(0, Math.min(20000, parsedTimeout))
      : 20000;

    if (!normalizedConfigId || !componentKey) return Promise.resolve(null);

    var readyToken = getReadyToken(normalizedConfigId, componentKey.split(','));
    if (readyToken) return Promise.resolve(readyToken);

    // Le clic checkout accélère seulement un travail déjà planifié. Il ne
    // relance pas de rendu et évite ainsi la création concurrente d'un produit
    // illustré et d'un produit neutre pour la même composition.
    if (!startPendingNow(normalizedConfigId)) return Promise.resolve(null);

    var deadline = Date.now() + boundedTimeout;
    return new Promise(function (resolve) {
      function poll() {
        var token = getReadyToken(normalizedConfigId, componentKey.split(','));
        if (token) {
          resolve(token);
          return;
        }

        var state = states.get(normalizedConfigId);
        var job = jobs.get(normalizedConfigId);
        if (
          !job || !state || state.status === 'failed' ||
          state.status === 'cancelled' || Date.now() >= deadline
        ) {
          resolve(null);
          return;
        }

        window.setTimeout(poll, 75);
      }

      poll();
    });
  }

  function cancelPending(configId) {
    var normalizedConfigId = String(configId || '').trim();
    if (!normalizedConfigId) return false;

    var state = states.get(normalizedConfigId);
    if (state && state.status === 'ready' && state.expiresAt > Date.now()) {
      return false;
    }

    return cancelJob(jobs.get(normalizedConfigId), true);
  }

  function remove(configId) {
    var normalizedConfigId = String(configId || '').trim();
    if (!normalizedConfigId) return;
    cancelJob(jobs.get(normalizedConfigId), false);
    states.delete(normalizedConfigId);
    removeStored(normalizedConfigId);
  }

  function validPhotoUrl(value) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      var url = new URL(value, location.origin);
      if (url.protocol !== 'https:' || !['cdn.shopify.com', location.hostname].includes(url.hostname)) return null;
      return url.href;
    } catch (_) { return null; }
  }

  function schedulePhoto(input) {
    if (!validPhotoUrl(input && input.imageUrl)) return false;
    return schedule(Object.assign({}, input, { photoUrl: input.imageUrl }));
  }

  function normalizeMaterialFromCart(items) {
    var values = [];
    items.forEach(function (item) {
      if (item && item.variant_title) values.push(item.variant_title);
      if (item && Array.isArray(item.options_with_values)) {
        item.options_with_values.forEach(function (option) {
          if (option && option.value) values.push(option.value);
        });
      }
    });

    var joined = values.join(' ').toLowerCase();
    if (/argent|silver/.test(joined)) return 'ARGENT';
    if (/\bor\b|dor[eé]|gold/.test(joined)) return 'OR';
    // La matière participe au digest, pas au rendu : cette valeur déterministe
    // permet de reconstruire les anciens paniers dont l'option est absente.
    return 'PANIER';
  }

  function scheduleFromCart(input) {
    var cart = input && input.cart;
    var backgroundUrl = input && typeof input.backgroundUrl === 'string'
      ? input.backgroundUrl.trim()
      : '';
    if (!cart || !Array.isArray(cart.items) || !backgroundUrl) return 0;

    var compositionGroups = new Map();
    cart.items.forEach(function (item) {
      var properties = item && item.properties;
      var configId = properties && String(properties._ConfigID || '').trim();
      if (!configId) return;
      if (!compositionGroups.has(configId)) compositionGroups.set(configId, []);
      compositionGroups.get(configId).push(item);
    });

    var scheduledCount = 0;
    compositionGroups.forEach(function (items, configId) {
      var catalogPhoto = items[0].properties._CompositionImage;
      if (catalogPhoto) {
        var material = items[0].properties._CompositionMaterial;
        if (!items.every(function (item) { return item.properties._CompositionImage === catalogPhoto && item.properties._CompositionMaterial === material; })) return;
        if (schedulePhoto({ configId: configId, imageUrl: catalogPhoto, material: material, componentIds: items.map(function (item) { return item.variant_id; }), onlyIfMissing: true })) scheduledCount += 1;
        return;
      }
      var componentIds = [];
      var layerMap = new Map();
      var invalid = false;

      items.forEach(function (item, itemIndex) {
        var properties = item && item.properties ? item.properties : {};
        var variantId = Number(item && item.variant_id);
        var src = typeof properties._OverlayImg === 'string' ? properties._OverlayImg.trim() : '';
        var left = finiteStyleNumber(properties._PosX);
        var top = finiteStyleNumber(properties._PosY);
        var width = finiteStyleNumber(properties._PosW);

        if (!Number.isSafeInteger(variantId) || variantId <= 0 || !src ||
            left === null || top === null || width === null || width <= 0) {
          invalid = true;
          return;
        }

        componentIds.push(variantId);
        // Même règle que le rendu du panier : la capture reconstruite doit être
        // pixel-identique à ce que la cliente vient de voir dans le drawer.
        var zIndex = width > 20 ? 5 : 10;
        // Deux lignes peuvent décrire un seul calque combiné. Les regrouper
        // évite de dessiner deux fois la même image tout en représentant tous
        // les variant IDs dans le jeton signé.
        var layerKey = [src, left, top, width].join('|');
        var layer = layerMap.get(layerKey);
        if (layer) {
          layer.variantIds.push(variantId);
        } else {
          layerMap.set(layerKey, {
            id: 'cart-' + itemIndex,
            src: src,
            variantIds: [variantId],
            style: {
              left: left,
              top: top,
              width: width,
              zIndex: zIndex,
              opacity: 1
            }
          });
        }
      });

      if (invalid || componentIds.length === 0) return;
      if (schedule({
        configId: configId,
        backgroundUrl: backgroundUrl,
        material: normalizeMaterialFromCart(items),
        componentIds: componentIds,
        layers: Array.from(layerMap.values()),
        onlyIfMissing: true
      })) {
        scheduledCount += 1;
      }
    });

    return scheduledCount;
  }

  window.SolsticeCompositionImages = Object.freeze({
    schedule: schedule,
    schedulePhoto: schedulePhoto,
    scheduleFromCart: scheduleFromCart,
    getReadyToken: getReadyToken,
    waitForReadyToken: waitForReadyToken,
    cancelPending: cancelPending,
    remove: remove
  });
})();
